import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Skeleton,
  Typography,
  Row,
  Col,
  Flex,
  Dropdown,
  Space,
  Modal,
  Descriptions,
  message,
  Alert,
  Badge,
  Tooltip,
} from "antd";
import {
  ClockCircleOutlined,
  SendOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type { TableProps } from "antd/es/table";
import { MenuProps } from "antd/lib";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import { Container } from "../ViewGerenciarPostagens/styles";
import { useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { listarMensagensPorCampanhaService } from "../../services/mensagensPessoaService";
import { campanhaService } from "../../services/campanhaService";

const { Title } = Typography;

// 🔹 Tipagem forte dos status
type StatusMensagem =
  | "AGUARDANDO_ENVIO"
  | "ENVIADA"
  | "CLICADA"
  | "RESPONDIDA"
  | "CANCELADA";

const STATUS: readonly StatusMensagem[] = [
  "AGUARDANDO_ENVIO",
  "ENVIADA",
  "CLICADA",
  "RESPONDIDA",
  "CANCELADA",
] as const;

// Type guard
function isStatusMensagem(val: any): val is StatusMensagem {
  return STATUS.includes(val as StatusMensagem);
}

export default function ViewGerenciarMensagens() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [messageApi, contextHolder] = message.useMessage();

  const params = new URLSearchParams(window.location.search);
  const campanhaId = Number(params.get("campanhaId")) || 0;

  const [mensagens, setMensagens] = useState<any[]>([]);
  const [nomeCampanha, setNomeCampanha] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [modalVisivel, setModalVisivel] = useState(false);
  const [mensagemSelecionada, setMensagemSelecionada] = useState<any | null>(null);
  const [statusFiltro, setStatusFiltro] = useState<StatusMensagem | null>(null);

  useEffect(() => {
    if (campanhaId) {
      fetchMensagens();
      fetchCampanhaNome();
    }
  }, [campanhaId]);

  const fetchMensagens = async () => {
    try {
      setLoading(true);
      const data = await listarMensagensPorCampanhaService(campanhaId);
      setMensagens(data ?? []);
      setErro(null);
    } catch (err: any) {
      console.error("Erro ao buscar mensagens:", err);
      setErro("Erro ao buscar mensagens.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCampanhaNome = async () => {
    try {
      const campanha = await campanhaService.buscarCampanhaPorIdService(campanhaId);
      if (campanha?.nome) setNomeCampanha(campanha.nome);
    } catch (err) {
      console.error("Erro ao buscar nome da campanha:", err);
      setNomeCampanha("Campanha desconhecida");
    }
  };

  // 🔹 Contagem de status com tipagem correta
  const contagemStatus: Record<StatusMensagem, number> = useMemo(() => {
    const base: Record<StatusMensagem, number> = {
      AGUARDANDO_ENVIO: 0,
      ENVIADA: 0,
      CLICADA: 0,
      RESPONDIDA: 0,
      CANCELADA: 0,
    };
    mensagens.forEach((msg) => {
      const st = msg.statusMensagem;
      if (isStatusMensagem(st)) {
        base[st] += 1;
      }
    });
    return base;
  }, [mensagens]);

  // 🔹 Definição dos badges com tipo forte
  const badges: { key: StatusMensagem; label: string; icon: React.ReactNode; color: string }[] = [
    { key: "AGUARDANDO_ENVIO", label: "Aguardando", icon: <ClockCircleOutlined style={{ color: "#faad14" }} />, color: "gold" },
    { key: "ENVIADA", label: "Enviada", icon: <SendOutlined style={{ color: "#1890ff" }} />, color: "blue" },
    { key: "CLICADA", label: "Clicada", icon: <MessageOutlined style={{ color: "#722ed1" }} />, color: "purple" },
    { key: "RESPONDIDA", label: "Respondida", icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />, color: "green" },
    { key: "CANCELADA", label: "Cancelada", icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />, color: "red" },
  ];

  const mensagensFiltradas = useMemo(() => {
    if (!statusFiltro) return mensagens;
    return mensagens.filter((m) => isStatusMensagem(m.statusMensagem) && m.statusMensagem === statusFiltro);
  }, [mensagens, statusFiltro]);

  const labelFiltroAtivo = badges.find((b) => b.key === statusFiltro)?.label || null;
  const iconeFiltroAtivo = badges.find((b) => b.key === statusFiltro)?.icon || null;

  const items: MenuProps["items"] = [{ key: "1", label: "Ver Mensagem" }];

  const handleMenuClick = (mensagem: any): MenuProps["onClick"] => (info) => {
    if (info.key === "1") {
      setMensagemSelecionada(mensagem);
      setModalVisivel(true);
    }
  };

  const columns: TableProps<any>["columns"] = [
    { title: "Nome", dataIndex: ["Pessoa", "nome"], key: "nome", align: "center", width: "20%", render: (v) => v || "—" },
    { title: "Telefone", dataIndex: ["Pessoa", "telefone"], key: "telefone", align: "center",width: "20%", render: (v) => v || "—" },
    { title: "Tipo Mensagem", dataIndex: ["TipoMensagem", "nome"], key: "tipoMensagem", align: "center",width: "20%", render: (v) => v || "—" },
    { title: "Status", dataIndex: "statusMensagem", key: "statusMensagem", align: "center",width: "20%", render: (v) => v || "—" },
    {
      title: "Data Envio",
      dataIndex: "dataEnvio",
      key: "dataEnvio",
      align: "center",
      width: "20%",
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
    {
  title: "Ações",
  key: "acoes",
  align: "center",
  width: "10%",
  render: (_, row) => (
    <Space size="middle">
      <Dropdown
        menu={{
          items,
          onClick: handleMenuClick(row),
        }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button type="text" icon={<SettingsIcon />} />
      </Dropdown>
    </Space>
  ),
}

  ];

  return (
    <Container>
      {contextHolder}
      {loading ? (
        <Skeleton active />
      ) : (
        <>
          {/* 🔹 Cabeçalho */}
          <Row justify="space-between" align="middle">
            <Col>
              <Flex align="center" style={{ marginTop: 15 }}>
                <a href={`/gerenciarCampanhas`}>
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>

            <Col flex="auto">
              <Flex align="center" justify="center" style={{ flexDirection: "column", textAlign: "center" }}>
                <Flex align="center" justify="center">
                  <HomeIcon color="primary" fontSize="large" />
                  <Title
                    level={3}
                    style={{
                      marginLeft: 10,
                      marginTop: 10,
                      fontSize: isMobile ? "1rem" : "2rem",
                      color: "rgb(0, 54, 68)",
                    }}
                  >
                    Mensagens — {nomeCampanha || "Carregando..."}
                    {labelFiltroAtivo && (
                      <span style={{ fontSize: "1.2rem", color: "#1890ff" }}>
                        {" "}
                        — {iconeFiltroAtivo} {labelFiltroAtivo}
                      </span>
                    )}
                  </Title>
                </Flex>

                {statusFiltro && (
                  <Button
                    type="link"
                    icon={<CloseOutlined />}
                    onClick={() => setStatusFiltro(null)}
                    style={{
                      color: "#ff4d4f",
                      fontWeight: 500,
                      marginTop: 5,
                    }}
                  >
                    Limpar filtro
                  </Button>
                )}
              </Flex>
            </Col>

            {/* 🔹 BADGES + contador */}
<Col>
  <Space
    size="large"
    align="center"
    wrap
    style={{ marginRight: 20, flexWrap: "wrap", justifyContent: "flex-end" }}
  >
    {badges.map((b) => {
      const ativo = statusFiltro === b.key;
      return (
        <Tooltip title={`Filtrar por ${b.label}`} key={b.key}>
          <div
            onClick={() => setStatusFiltro(ativo ? null : b.key)}
            style={{
              cursor: "pointer",
              border: ativo ? `2px solid ${b.color}` : "1px solid #d9d9d9",
              borderRadius: 8,
              padding: "4px 8px",
              transition: "all 0.2s ease",
              backgroundColor: ativo ? `${b.color}20` : "#fff",
            }}
          >
            <Badge
              count={contagemStatus[b.key] > 99 ? "99+" : contagemStatus[b.key]}
              color={b.color}
              showZero
            >
              <span style={{ marginLeft: 8, fontWeight: ativo ? 600 : 400 }}>
                {b.icon} {b.label}
              </span>
            </Badge>
          </div>
        </Tooltip>
            );
          })}

       {/* 🧮 Contador total (dinâmico) */}
        <div
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            backgroundColor: "#f0f2f5",
            fontWeight: 600,
            color: "#003644",
            fontSize: "0.95rem",
                      }}
                    >
              {statusFiltro ? (
                <>
                  Total filtrado:{" "}
                  <span style={{ color: "#1890ff" }}>
                    {mensagensFiltradas.length.toLocaleString("pt-BR")}
                  </span>{" "}
                  mensagens{" "}
                  <span style={{ color: "#999", fontWeight: 400 }}>
                    (de {mensagens.length.toLocaleString("pt-BR")} no total)
                  </span>
                </>
              ) : (
                <>
                  Total:{" "}
                  <span style={{ color: "#1890ff" }}>
                    {mensagens.length.toLocaleString("pt-BR")}
                  </span>{" "}
                  mensagens
                </>
              )}
            </div>

        </Space>
      </Col>

          </Row>

          {/* 🔹 Tabela */}
          {erro ? (
            <Alert message="Erro ao carregar mensagens" description={erro} type="error" showIcon />
          ) : (
            <Row>
              <Col span={24}>
                <Flex justify="center" style={{ marginTop: 20, overflowX: "auto", width: "100%" }}>
                  <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={mensagensFiltradas}
                    bordered
                    pagination={{ pageSize: 10 }}
                    style={{ width: "100%", minWidth: 900 }}
                  />
                </Flex>
              </Col>
            </Row>
          )}
        </>
      )}
      {/* 🔹 Modal Ver Mensagem */}
<Modal
  title="Detalhes da Mensagem"
  open={modalVisivel}
  onCancel={() => setModalVisivel(false)}
  footer={null}
  centered
  width={700}
>
  {mensagemSelecionada ? (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label="Pessoa">
        {mensagemSelecionada.Pessoa?.nome || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Telefone">
        {mensagemSelecionada.Pessoa?.telefone || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Tipo Mensagem">
        {mensagemSelecionada.TipoMensagem?.nome || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Título">
        {mensagemSelecionada.titulo || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Conteúdo">
        <div
          style={{
            whiteSpace: "pre-wrap",
            background: "#fafafa",
            padding: "10px",
            borderRadius: 6,
            minHeight: 100,
          }}
        >
          {mensagemSelecionada.conteudo || "—"}
        </div>
      </Descriptions.Item>

      <Descriptions.Item label="Status">
        {mensagemSelecionada.statusMensagem || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Data Envio">
        {mensagemSelecionada.dataEnvio
          ? dayjs(mensagemSelecionada.dataEnvio).format("DD/MM/YYYY HH:mm")
          : "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Data Resposta">
        {mensagemSelecionada.dataResposta
          ? dayjs(mensagemSelecionada.dataResposta).format("DD/MM/YYYY HH:mm")
          : "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Restrição">
        {mensagemSelecionada.restricao || "—"}
      </Descriptions.Item>

      <Descriptions.Item label="Próxima Data Envio">
        {mensagemSelecionada.proximaDataEnvio
          ? dayjs(mensagemSelecionada.proximaDataEnvio).format("DD/MM/YYYY HH:mm")
          : "—"}
      </Descriptions.Item>
    </Descriptions>
  ) : (
    <p>Selecione uma mensagem para visualizar.</p>
  )}
</Modal>

    </Container>
  );
}
